import { useCallback, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import clsx from 'clsx'
import { rem } from 'polished'
import CalendarDatePicker from './BedbankDatePicker/CalendarDatePicker'
import OfferDatePicker from '../Filters/DatePicker/OfferDatePicker'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { isFlashCalendarEnabled } from 'selectors/offerPage/bedbankOfferPageSelectors'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import eases from 'styles/tools/eases'
import BodyText from 'LuxKit/Typography/BodyText'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { INTL_SHORT_DAY_NAME_DAY_MONTH } from 'constants/dateFormats'
import LineArrowRightIcon from 'LuxKit/Icons/line/LineArrowRightIcon'
import noop from 'lib/function/noop'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import type { AccommodationMobileDayCellSelectHandler } from 'components/AccommodationKit/AccommodationCalendar/AccommodationCalendar.types'
import moment from 'moment'

const FooterContents = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  transform: translateY(100%);
  transition: transform 0.3s ${eases.expo.in};

  &.visible {
    position: unset;
    transform: translateY(0);
    transition: transform 0.3s ${eases.expo.out};
  }
`

const CalendarDatePickerWrapper = styled.div`
  padding-top: ${rem(20)};
`

interface Props {
  isCalendarEnabled: boolean
  onClose?: () => void
  onBack?: () => void
  checkInDate?: string
  checkOutDate?: string
}

function BedbankSidebarFilterCalendar(props: Props) {
  const {
    isCalendarEnabled,
    onClose = noop,
    onBack,
    checkInDate,
    checkOutDate,
  } = props

  const intl = useIntl()
  const [dates, setDates] = useState<{ checkIn?: string, checkOut?: string }>({
    checkIn: checkInDate,
    checkOut: checkOutDate,
  })
  const dispatch = useOfferPageDispatch()
  const intlDateFormatter = useIntlDateFormatter()
  const modalContext = useModalElementContext()

  // const [selectedMonth, setSelectedMonth] = useState<string | undefined>(selectedDay?.checkIn)

  const onResetDates = useCallback(() => {
    setDates({ checkIn: undefined, checkOut: undefined })
  }, [])

  const onApply = useCallback(() => {
    dispatch({ type: OfferPageActions.setDates, checkIn: dates.checkIn, checkOut: dates.checkOut })
    modalContext.resolve()
  }, [dates.checkIn, dates.checkOut, dispatch, modalContext])

  const onModalClose = useCallback(() => {
    onClose()
    if (!(dates.checkIn && !dates.checkOut)) {
      // only update state if it's not an 'invalid' (has check in, but not check out) selection
      dispatch({ type: OfferPageActions.setDates, checkIn: dates.checkIn, checkOut: dates.checkOut })
    }
    modalContext.resolve()
  }, [onClose, dispatch, dates.checkIn, dates.checkOut, modalContext])

  const onModalBack = useCallback(() => {
    dispatch({ type: OfferPageActions.setDates, checkIn: dates.checkIn, checkOut: dates.checkOut })
    onBack?.()
  }, [dates.checkIn, dates.checkOut, dispatch, onBack])

  const scrollerRef = useRef<HTMLDivElement>(null)

  // temporarily disabled until we have updated designs for the month view when empty
  // const onShowMonth = useCallback(() => setSelectedMonth(undefined), [])
  // const onSelectMonth = useCallback<AccommodationCalendarYearSelectHandler>((month) => {
  //   setSelectedMonth(formatIsoDate(new Date(month.year, month.month, 1)))
  // }, [])

  const onDatesChange = useCallback<AccommodationMobileDayCellSelectHandler>(day => {
    if (dates.checkIn && !dates.checkOut && moment(day.date).isAfter(dates.checkIn)) {
      setDates({ checkIn: dates.checkIn, checkOut: day.date })
    } else {
      setDates({ checkIn: day.date, checkOut: undefined })
    }
  }, [dates.checkIn, dates.checkOut])

  useEffect(() => {
    setDates({
      checkIn: checkInDate,
      checkOut: checkOutDate,
    })
  }, [checkInDate, checkOutDate])

  return (
    <>
      <ModalHeader
        title={intl.formatMessage({
          id: 'e0W2MMc4zAKrO14Q37QL',
          defaultMessage: 'Select your dates',
          description: 'Title for bedbank date selection modal in the date selection or calendar picker',
        })}
        onCloseButtonClick={onModalClose}
        onBackButtonClick={onBack ? onModalBack : undefined}
      >
      </ModalHeader>
      <ModalBody ref={scrollerRef}>
        <ModalContent>
          <CalendarDatePickerWrapper>
            {isCalendarEnabled && <CalendarDatePicker single />}
            {!isCalendarEnabled && <OfferDatePicker
              scrollerRef={scrollerRef}
              size="small"
              // onBackToMonth={onShowMonth}
              // onMonthSelect={onSelectMonth}
              // selectedMonth={selectedMonth}
              variant="day-only"
              checkInDate={dates.checkIn}
              checkOutDate={dates.checkOut}
              onDatesChange={onDatesChange}
            />}
          </CalendarDatePickerWrapper>
        </ModalContent>
      </ModalBody>
      <FooterContents className={clsx({ visible: !!dates.checkIn && !!dates.checkOut })}>
        <ModalFooter>
          <VerticalSpacer gap={8}>
            {dates.checkIn && dates.checkOut && <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
              <BodyText variant="medium">
                <Group direction="horizontal" verticalAlign="center">
                  <span>{intlDateFormatter(dates.checkIn, INTL_SHORT_DAY_NAME_DAY_MONTH)}</span>
                  <LineArrowRightIcon size="XS" />
                  <span>{intlDateFormatter(dates.checkOut, INTL_SHORT_DAY_NAME_DAY_MONTH)}</span>
                </Group>
              </BodyText>
              <TextButton kind="tertiary" horizontalOutdent="end" onClick={onResetDates}>
                {intl.formatMessage({
                  id: 'H3b8aYHJ9Ay4jvljZnXI',
                  defaultMessage: 'Reset',
                  description: 'Button to reset selected dates in bedbank sidebar filter calendar',
                })}
              </TextButton>
            </Group>}
            <TextButton kind="primary" size="large" type="submit" fit="mobile-full-width" onClick={onApply}>
              {intl.formatMessage({
                id: 'wxOWSsQ7esvgwKDlL9Oj',
                defaultMessage: 'Apply',
                description: 'Button to apply selected dates in bedbank sidebar filter calendar',
              })}
            </TextButton>
          </VerticalSpacer>
        </ModalFooter>
      </FooterContents>
    </>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  isCalendarEnabled: isFlashCalendarEnabled(state, appState),
  checkInDate: state.checkInDate,
  checkOutDate: state.checkOutDate,
}), BedbankSidebarFilterCalendar)
